import { useEffect, useState } from "react";
import { Plus, Trash, ToggleLeft, ToggleRight } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { ROLE_LABEL, formatDateTime } from "../lib/constants";
import { toast } from "sonner";

const ROLES = ["sales", "admin", "finance", "warehouse", "super_admin"];

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "sales", phone: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/users", form);
      toast.success("User created");
      setForm({ name: "", email: "", password: "", role: "sales", phone: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { active: !u.active });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.name}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="p-6 md:p-10" data-testid="users-page">
      <header className="mb-6">
        <div className="overline mb-2">// People</div>
        <h1 className="font-display font-black text-4xl tracking-tighter">User management</h1>
      </header>

      <section className="grid-line bg-white mb-6">
        <div className="border-b border-[var(--line)] px-5 py-3 font-display font-bold">Add new user</div>
        <form onSubmit={create} className="p-5 grid grid-cols-1 md:grid-cols-6 gap-3" data-testid="create-user-form">
          <input className="input-tech" placeholder="Full name" required value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-user-name" />
          <input type="email" className="input-tech" placeholder="Email" required value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" />
          <input type="text" className="input-tech" placeholder="Initial password" required value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-user-password" />
          <input type="tel" className="input-tech" placeholder="Phone (+234…)" value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="new-user-phone" />
          <select className="select-tech" value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="new-user-role">
            {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
          </select>
          <button disabled={creating} className="btn-solar inline-flex items-center justify-center gap-2" data-testid="create-user-btn">
            <Plus size={16} weight="bold" /> {creating ? "Creating…" : "Create"}
          </button>
        </form>
      </section>

      <section className="grid-line bg-white">
        {loading ? (
          <div className="p-10 overline text-center">Loading…</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Role</th>
                <th>Status</th>
                <th>Joined</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.email}`}>
                  <td className="font-medium">{u.name}</td>
                  <td className="font-mono-tech text-xs">{u.email}</td>
                  <td className="font-mono-tech text-xs">{u.phone || "—"}</td>
                  <td><span className="pill" style={{ color: "#2563EB" }}>{ROLE_LABEL[u.role]}</span></td>
                  <td>
                    <button onClick={() => toggleActive(u)} className="inline-flex items-center gap-1 text-xs uppercase tracking-widest font-bold">
                      {u.active ? <ToggleRight size={20} color="#059669" weight="fill" /> : <ToggleLeft size={20} color="#9CA3AF" weight="fill" />}
                      {u.active ? "Active" : "Disabled"}
                    </button>
                  </td>
                  <td className="text-xs text-[var(--muted)]">{formatDateTime(u.created_at)}</td>
                  <td>
                    <button onClick={() => remove(u)} className="text-xs uppercase tracking-widest font-bold text-[var(--danger)] inline-flex items-center gap-1">
                      <Trash size={14} /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
